import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  CheckCircle, Target, Eye, ShieldCheck, TrendingUp, Users, Clock, ChevronRight
} from 'lucide-react';

const checklist = [
  'Meticulous, knowledge-driven Forex strategy',
  'Transparent daily ROI, visible right in your dashboard',
  'Real technology backing every trading decision',
  'A growing community built on teamwork, not hype',
];

const whyUs = [
  { icon: ShieldCheck, title: 'Transparency', text: 'Every ROI credit, referral payout, and withdrawal is logged and visible in your dashboard — nothing happens in the dark.' },
  { icon: TrendingUp, title: 'Disciplined Strategy', text: 'Our trading approach is built on technical analysis and risk management, not guesswork or speculation.' },
  { icon: Users, title: 'Community First', text: 'Your network grows with structured support up to 30 levels deep, rewarding real teamwork.' },
  { icon: Clock, title: 'Always Available', text: 'Track your portfolio and request withdrawals any day of the week, right from your account.' },
];

export default function About() {
  return (
    <>
      <Navbar />

      {/* PAGE BANNER */}
      <section className="page-banner">
        <div className="page-banner-bg" style={{ backgroundImage: 'url(/mlm2.png)' }} />
        <div className="page-banner-overlay" />
        <div className="container page-banner-content">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="breadcrumb-item">Home</Link>
            <ChevronRight size={14} className="breadcrumb-sep" />
            <span className="breadcrumb-item breadcrumb-current">About</span>
          </nav>
          <h1 className="page-banner-title">
            ABOUT <span style={{ color: 'var(--primary-light, #6fa0ff)' }}>TRIVENI PAY</span>
          </h1>
          <p className="page-banner-sub">
            A disciplined, technology-backed approach to Forex investing — built for people who value transparency over promises.
          </p>
        </div>
      </section>

      {/* IMAGE + CONTENT SPLIT */}
      <section className="section">
        <div className="container">
          <div className="about-split">
            {/* LEFT — IMAGE */}
            <div className="about-visual">
              <img src="/mlm2.png" alt="Triveni Pay network of investors" />
              <div className="about-visual-badge">
                <div className="about-visual-badge-value">30</div>
                <div className="about-visual-badge-label">Income Levels Deep</div>
              </div>
            </div>

            {/* RIGHT — CONTENT */}
            <div>
              <div className="hero-eyebrow" style={{ color: 'var(--primary)', borderColor: 'var(--border-gold-bright)', background: 'rgba(var(--primary-rgb),0.08)' }}>
                WHO WE ARE
              </div>
              <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.6rem, 3vw, 2.3rem)', fontWeight: 700, marginBottom: 18, textTransform: 'uppercase', lineHeight: 1.25 }}>
                Disciplined Strategy, <span style={{ color: 'var(--primary)' }}>Built For Long-Term Trust</span>
              </h2>
              <p style={{ color: 'var(--text-light)', lineHeight: 1.8, marginBottom: 16 }}>
                Triveni Pay is dedicated to offering a reliable Forex investment and trading platform. Setting itself apart through a meticulous and knowledge-driven strategy, we aim to deliver optimal returns while instilling confidence in every investor who joins us.
              </p>
              <p style={{ color: 'var(--text-light)', lineHeight: 1.8, marginBottom: 28 }}>
                We concentrate on the financial intricacies and technical fundamentals of Forex assets, so growth on our platform is the result of process — not chance.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 32 }}>
                {checklist.map((item) => (
                  <div key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <CheckCircle size={20} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
                    <span style={{ color: 'var(--text-light)', fontSize: '0.95rem' }}>{item}</span>
                  </div>
                ))}
              </div>

              <div className="hero-stat-row" style={{ marginTop: 0, justifyContent: 'flex-start', marginBottom: 32 }}>
                {[
                  { val: '1%', label: 'Daily ROI' },
                  { val: '365', label: 'Days Valid' },
                  { val: '₹500', label: 'Min Package' },
                ].map(stat => (
                  <div key={stat.label} style={{ textAlign: 'left' }}>
                    <div className="stat-val" style={{ color: 'var(--primary)' }}>{stat.val}</div>
                    <div className="stat-lbl" style={{ color: 'var(--text-muted)' }}>{stat.label}</div>
                  </div>
                ))}
              </div>

              <Link to="/register" className="btn btn-primary btn-lg">Join Now Free</Link>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="section section-alt section-relative">
        <div className="glow-orb-field">
          <div className="glow-orb glow-orb-gold"></div>
          <div className="glow-orb glow-orb-blue"></div>
        </div>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">MISSION & <span>VISION</span></h2>
            <div className="section-line"></div>
          </div>
          <div className="grid-2">
            {[
              { title: 'Mission', icon: Target, text: 'To provide a reliable Forex investment and trading platform that empowers individuals to achieve financial growth through knowledge-driven strategies and transparent services.' },
              { title: 'Vision', icon: Eye, text: 'To be a globally trusted leader in the Forex trading industry, recognized for innovation, integrity, and delivering consistent value to our investors and partners.' },
            ].map(item => (
              <div key={item.title} className="info-box" style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center', color: 'var(--primary)' }}><item.icon size={40} /></div>
                <div className="info-box-title">{item.title}</div>
                <p className="info-box-text">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">WHY CHOOSE <span>US</span></h2>
            <div className="section-line"></div>
            <p className="section-desc">The principles that guide every decision on the platform.</p>
          </div>
          <div className="grid-2">
            {whyUs.map(item => (
              <div key={item.title} className="income-type-card">
                <div className="income-type-icon"><item.icon size={24} /></div>
                <div>
                  <div className="income-type-name">{item.title}</div>
                  <div className="income-type-desc">{item.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section section-relative" style={{ background: 'linear-gradient(135deg, rgba(var(--secondary-rgb),0.1), rgba(var(--primary-rgb),0.08))' }}>
        <div className="glow-orb-field">
          <div className="glow-orb glow-orb-gold" style={{ top: 'auto', bottom: -80, left: '10%' }}></div>
          <div className="glow-orb glow-orb-blue" style={{ top: -60, right: '15%', bottom: 'auto' }}></div>
        </div>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'Rajdhani', fontSize: 'clamp(1.8rem, 4vw, 3rem)', fontWeight: 700, marginBottom: 16 }}>
            READY TO <span style={{ color: 'var(--primary)' }}>GET STARTED?</span>
          </h2>
          <p style={{ color: 'var(--text-light)', marginBottom: 32, maxWidth: 500, margin: '0 auto 32px' }}>
            Join thousands of investors building their financial future with Triveni Pay. Start with just ₹500 today.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="btn btn-primary btn-lg">Join Now It's Free</Link>
            <Link to="/login" className="btn btn-outline btn-lg">Login</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}